import React from 'react';
import BaseRadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import makeStyles from '@material-ui/core/styles/makeStyles';
import createStyles from '@material-ui/core/styles/createStyles';
import clsx from 'clsx';
import Radio from '../checkboxes/Radio';

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      marginLeft: 0,
      marginRight: 0,
      marginTop: 2,
      marginBottom: 2,
      padding: '7px 0',
    },
    rootBordered: {
      marginLeft: 0,
      marginRight: 0,
      marginTop: theme.spacing(),
      marginBottom: theme.spacing(),
      borderRadius: 3,
      borderColor: theme.palette.grey.A100,
      borderWidth: 1,
      borderStyle: 'solid',
      backgroundColor: theme.palette.common.white,
    },
    label: {
      color: theme.palette.text.primary,
      fontSize: 16,
      lineHeight: 1.5,
      [theme.breakpoints.down('sm')]: {
        fontSize: 12,
      },
    },
    icon: {
      paddingLeft: 2,
      paddingTop: 7,
      paddingBottom: 7,
      paddingRight: 8,
    },
    listLogo: {
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
      '& > div': {
        '& div': {
          marginLeft: 10,
        },
      },
    },
    labelBold: {
      fontWeight: 700,
    },
    selected: {
      borderColor: theme.palette.secondary.main,
    },
    error: {
      borderColor: 'red !important',
    },
    customMarginRight: {
      marginRight: 20,
    },
    radioContainer: {
      backgroundColor: 'transparent',
      borderRadius: 0,
      margin: 0,
      padding: 0,
    },
    radioContainerExpanded: {
      backgroundColor: theme.palette.grey['100'],
      borderRadius: 3,
      margin: '10px -10px 15px -10px',
      padding: 10,
    },
    content: {
      maxHeight: 0,
      opacity: 0,
      overflow: 'hidden',
    },
    contentExpanded: {
      maxHeight: '350px',
      opacity: 1,
    },
  }),
);

interface Props {
  name?: any;
  value?: any;
  onChange?: any;
  labelBold?: any;
  items?: any;
  isBordered?: any;
  isError?: any;
  'aria-label'?: any;
  marginRight?: boolean;
  row?: boolean;
  inputRef?: React.Ref<any>;
  inlineLabelClass?: string;
}

const RadioGroup: React.FC<Props> = ({
  name,
  value,
  onChange,
  labelBold,
  items,
  isBordered,
  isError,
  'aria-label': ariaLabel,
  marginRight,
  row,
  inputRef,
  inlineLabelClass,
}) => {
  const classes = useStyles();
  return (
    <BaseRadioGroup
      row={row}
      name={name}
      value={value}
      onChange={onChange}
      aria-label={ariaLabel || name}
      ref={inputRef}
    >
      {items.map((item: any, index: number) => {
        const selectedItem = item.value === value;
        const hasItemContent = !!item.content;

        return (
          <div
            key={item.value || index}
            className={selectedItem && hasItemContent ? classes.radioContainerExpanded : classes.radioContainer}
          >
            <FormControlLabel
              value={item.value}
              label={
                <div
                  onClick={onChange}
                  onKeyUp={onChange}
                  role="radio"
                  tabIndex={index}
                  aria-checked={value === item.value}
                  className={clsx(
                    classes.listLogo,
                    marginRight && classes.customMarginRight,
                    labelBold && classes.labelBold,
                    inlineLabelClass,
                  )}
                >
                  {item.label}
                  {item.icon ? item.icon : ''}
                </div>
              }
              disabled={item.disabled}
              control={
                <Radio className={classes.icon} checked={value === item.value} onClick={onChange} value={item.value} />
              }
              key={item.id || item.label}
              classes={{
                label: classes.label,
                root: isBordered ? classes.rootBordered : classes.root,
              }}
              className={clsx(selectedItem && classes.selected, isError && classes.error)}
            />
            <div className={selectedItem && hasItemContent ? classes.contentExpanded : classes.content}>
              {hasItemContent && item.content}
            </div>
          </div>
        );
      })}
    </BaseRadioGroup>
  );
};

RadioGroup.defaultProps = {
  labelBold: false,
  isBordered: false,
  row: false,
  isError: false,
  name: '',
  'aria-label': 'RadioGroup',
  marginRight: false,
};

export default RadioGroup;
